import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  classificationOrFallback,
  comparePriority,
  listAllBriefBuckets,
  loadMoreStaffReview,
  type ZoneCursors,
  reassessBrief,
  restoreBrief,
  type BriefListItem,
  type PartnerBriefStatus,
  type Stage1Classification,
} from '../../lib/partner-briefs-api';
import { zoneByCode } from '../../lib/zones';
import { useZoneReadReport } from '../../components/ZoneContext';
import { ZoneCell, ZoneFilter, ZoneFanoutWarning, matchesZone } from '../../components/ZoneIndicators';
import { scoreHueColor } from './BriefDetailPage';
import { useStaffMe } from '../../hooks/useStaffMe';
import { DeleteBriefModal } from './DeleteBriefModal';
import { toastFromError, useToast } from '../../components/ToastProvider';
import { FilterPills } from '../../components/FilterPills';
import { FilterSearch } from '../../components/FilterSearch';
import { currentTimeZone, formatDate } from '../../lib/time-format';

/**
 * Applications tab content — POW Brief queue for Phase 5d-1 (Decision 5-27).
 *
 * Loads the active review queue from the admin-api by querying TWO statuses
 * in parallel — `human_review_required` (urgent, amber-indicated) and
 * `staff_review` (the default review-pending bucket) — and merges them
 * client-side per the sort key in comparePriority(): HRR first, then
 * staff_review, then anything else, with newest-first within each bucket.
 *
 * The list endpoint pages at 25 per status. At MVP volume (low tens of
 * pending Briefs) two parallel single-page fetches cover the visible queue
 * without compound paging gymnastics. A "Load more staff_review" button
 * appears when the staff_review bucket has a cursor — HRR is typically
 * small enough that the first 25 are always all of them.
 *
 * Row layout mirrors `phase-5d-decision-9-revised.html` View 2: company +
 * Stage 1 classification chip · contact (email + CN + date) · meta (website,
 * jurisdiction, assessment count) · POW score + Strong Fit bonus chip.
 * Action buttons (Approve / Reject / Review) inline on the right.
 *
 * Clicking the row navigates to /admin/partners/:applicationId where Phase
 * C builds the Brief detail view + staff action zone.
 */

/** Pokemon-Go-style filter groups. Each pill toggles visibility of one
 *  group; the group's pill caption is the operator-facing label. The
 *  underlying status mapping lives in groupOfBrief() below. Order here
 *  controls pill order in the UI.
 *
 *  AI-rejected vs admin-rejected split (UAT 30 May 2026): operators
 *  need to differentiate Stage 1 / Stage 2 agent rejections from
 *  human operator rejections (the agent's call may need overriding;
 *  the admin's call is final). When/if AI auto-approve lands later
 *  (currently every approve is admin-driven), the approved group will
 *  split similarly. */
type QueueGroup = 'pending' | 'review' | 'failed' | 'onboarding' | 'active' | 'rejected_ai' | 'rejected_admin' | 'deleted';

// 30 May 2026 (UAT iteration 4) — Approved + AI Approved pills folded
// into Onboarding. "Approved" was confusing as a standalone state
// because every approved Brief is in a transitional in-between (waiting
// for backlink verification on the subscriber path, or applicant sign-up
// on the Manual Add path). The new mental model:
//
//   Pre-Active gates = Onboarding pill   (approved, pending_backlink_
//                                         verification, manual_add_invited,
//                                         manual_add_expired)
//   Live partner     = Active pill       (active, suspended)
//
// AI vs admin decision-authority distinction stays in the ROW pill
// (🤖 AI approved / 👤 Admin approved) for at-a-glance triage; just no
// longer a separate filter group. AI-approved Briefs still appear in
// Review so admin can override before they progress further.
const QUEUE_GROUPS: Array<{ key: QueueGroup; label: string; icon: string; superAdminOnly?: boolean }> = [
  { key: 'pending', label: 'Pending', icon: '⏳' },
  { key: 'review', label: 'Review', icon: '👁' },
  { key: 'failed', label: 'Failed', icon: '⚠' },
  { key: 'onboarding', label: 'Onboarding', icon: '🚀' },
  { key: 'active', label: 'Active', icon: '🟢' },
  { key: 'rejected_ai', label: 'AI Rejected', icon: '🤖' },
  { key: 'rejected_admin', label: 'Admin Rejected', icon: '👤' },
  { key: 'deleted', label: 'Deleted', icon: '🗑', superAdminOnly: true },
];

/** Map a Brief's DDB status to one OR MORE filter groups. A Brief
 *  can belong to multiple groups (UAT iteration 3 reversal): AI
 *  rejections appear under both "AI rejected" (so they're filterable
 *  on their own) AND "Review" (so the default Review filter still
 *  surfaces them — admins may want to override an AI rejection).
 *  When AI auto-approve lands later, those Briefs should similarly
 *  appear in both Review (for admin sign-off) and a future
 *  approved_ai pill. */
function groupsOfBrief(b: BriefListItem): QueueGroup[] {
  switch (b.status) {
    case 'pending_validation':
    case 'pending_reassessment':
      return ['pending'];
    case 'agent_failed':
      return ['failed'];
    case 'approved':
    case 'pending_backlink_verification':
      // 30 May 2026 — backlink window removed. New Approves write
      // status='active' directly; these two statuses are legacy for
      // pre-change Briefs. Map to Active pill so the 6 existing partners
      // surface correctly. AI-approved legacy ones (no reviewedBy) still
      // surface in Review for admin sign-off.
      return b.reviewedBy ? ['active'] : ['review', 'active'];
    case 'manual_add_invited':
    case 'manual_add_expired':
      // Manual Add Partners between invitation send + applicant sign-up.
      // Distinct from Active because the applicant hasn't created their
      // Cognito account yet.
      return ['onboarding'];
    case 'active':
      // Live partner. AI auto-approve (no reviewedBy) also lands here
      // now — surfaces in Review for admin sign-off / override.
      return b.reviewedBy ? ['active'] : ['review', 'active'];
    case 'suspended':
      // Paused but reactivatable — stays in Active pill alongside live.
      return ['active'];
    case 'rejected_auto':
    case 'rejected_poor_fit':
      // AI-driven decisions need admin attention — operator may want to
      // override (Restore + Reassess). Surfaces under Review AND the
      // dedicated AI rejected pill.
      return ['review', 'rejected_ai'];
    case 'rejected':
      // Admin clicked Reject — terminal, no override path needed in queue.
      return ['rejected_admin'];
    case 'deleted':
      return ['deleted'];
    default:
      // staff_review, pending_response, info_requested, human_review_required
      return ['review'];
  }
}

/** Sortable column keys. 'priority' is the default and uses the
 *  existing comparePriority (cross-bucket ordering). Other keys are
 *  field-based — UI exposes them as clickable column-header chips
 *  with up/down arrow indicators. */
type SortKey = 'priority' | 'website' | 'submitted' | 'score' | 'name';
type SortDir = 'asc' | 'desc';
interface SortState { key: SortKey; dir: SortDir }

/** Default sort direction per column — what feels natural on first
 *  click. Score / Submitted default to descending (high first / newest
 *  first); website + name default ascending (A→Z). */
const DEFAULT_DIR: Record<SortKey, SortDir> = {
  priority: 'desc',
  website: 'asc',
  submitted: 'desc',
  score: 'desc',
  name: 'asc',
};

function sortBriefs(items: BriefListItem[], sort: SortState): BriefListItem[] {
  if (sort.key === 'priority') return items;
  const factor = sort.dir === 'asc' ? 1 : -1;
  const cmp = (a: BriefListItem, b: BriefListItem): number => {
    switch (sort.key) {
      case 'submitted':
        return factor * a.submittedAt.localeCompare(b.submittedAt);
      case 'score': {
        // Nulls always last regardless of direction — keep undecided
        // Briefs at the bottom of the score sort.
        const av = a.score, bv = b.score;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return factor * (av - bv);
      }
      case 'website':
        return factor * (a.websiteUrl || '').localeCompare(b.websiteUrl || '');
      case 'name':
        return factor * a.businessName.localeCompare(b.businessName);
      default:
        return 0;
    }
  };
  return [...items].sort(cmp);
}

interface QueueState {
  status: 'loading' | 'loaded' | 'error';
  reviewItems: BriefListItem[];         // staff_review + pending_response + info_requested + HRR
  pendingItems: BriefListItem[];        // pending_validation + pending_reassessment
  rejectedItems: BriefListItem[];       // rejected_poor_fit + rejected_auto + rejected
  closedItems: BriefListItem[];         // approved + pending_backlink_verification (Approved pill)
  onboardingItems: BriefListItem[];     // manual_add_invited + manual_add_expired (Onboarding pill)
  activeItems: BriefListItem[];         // active + suspended (Active pill — replaces Active Partners tab)
  failedItems: BriefListItem[];
  deletedItems: BriefListItem[];
  /**
   * ADR-122 Pattern 1 — one `staff_review` cursor PER ZONE, keyed by zone code.
   *
   * ⭐ A CURSOR IS ZONE-LOCAL. The single `reviewCursor` this replaced could only page one
   * jurisdiction; with two zones it would have paged the home zone and silently stopped
   * offering the other zone's remaining rows, which on a review queue means briefs nobody
   * ever sees. "Load more" now stays available while ANY zone has more.
   */
  reviewCursors: ZoneCursors;
  /** Labels of zones that did not answer the last load. */
  failedZones: string[];
  error?: string;
}

export function ApplicationsQueue() {
  const navigate = useNavigate();
  const toast = useToast();
  const me = useStaffMe();
  const isSuperAdmin = me.status === 'loaded' && me.me?.role === 'super_admin';
  // Pokemon-Go-style group filter pills (UAT 30 May 2026). Always a
  // Set — empty Set means "no filter pills active → no Briefs visible"
  // (with a hint to click a pill or "Show all"). Previously the empty
  // Set auto-cleared to null which meant "show all" and rendered every
  // pill as visually active — UAT 30 May 2026 reported this as confusing
  // (operator clicked the last selected pill expecting deselection, got
  // "everything selected" instead). Removing the null path fixes that.
  const [groupFilter, setGroupFilter] = useState<Set<QueueGroup>>(() => new Set(['review']));
  // ADR-122 Pattern 1 — narrows the rendered queue to one zone; 'all' is the default.
  const [zoneFilter, setZoneFilter] = useState('all');
  const [sort, setSort] = useState<SortState>({ key: 'priority', dir: 'desc' });
  // Free-text keyword search across business name + website (UAT
  // iteration 3). Client-side filter over the loaded merged list so it
  // composes with the pill filter + sort.
  const [searchQuery, setSearchQuery] = useState('');
  const [state, setState] = useState<QueueState>({
    status: 'loading',
    reviewItems: [],
    pendingItems: [],
    rejectedItems: [],
    closedItems: [],
    onboardingItems: [],
    activeItems: [],
    failedItems: [],
    deletedItems: [],
    reviewCursors: {},
    failedZones: [],
  });
  const [loadingMore, setLoadingMore] = useState(false);
  // Row-action transient state. Lets us disable the icon + show a
  // brief spinner without blowing up the whole queue.
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  // Counter of consecutive background-refresh failures. Surfaced as a
  // tiny banner after 3 in a row so the operator sees something is
  // wrong, but never replaces the queue UI.
  const [refreshErrors, setRefreshErrors] = useState(0);
  // Delete modal target — when set, the DeleteBriefModal is open over
  // that Brief. The modal handles both soft and hard delete internally
  // and includes the fresh-auth pre-check.
  const [deleteTarget, setDeleteTarget] = useState<BriefListItem | null>(null);

  // Track whether any visible row is still pending so the poll only
  // runs when there's actually something to watch. Once the queue
  // settles into HRR + staff_review (no pending rows), polling stops —
  // no more 5s visible refresh.
  const hasPending = state.pendingItems.length > 0;

  // ADR-123 Decision 1 — the navigation bar names the zones in this queue and their counts,
  // counted across every bucket (the group pills narrow what is shown, not what the zones hold).
  useZoneReadReport({
    rows: state.status === 'loaded'
      ? [
          ...state.reviewItems, ...state.pendingItems, ...state.rejectedItems, ...state.closedItems,
          ...state.onboardingItems, ...state.activeItems, ...state.failedItems, ...state.deletedItems,
        ]
      : null,
    failedZones: state.failedZones,
    filter: zoneFilter,
  });

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async (opts?: { background?: boolean }) => {
      const isBackground = opts?.background === true;
      // ONE admin-api call returns every status bucket. Replaces 10x
      // parallel client→Lambda invocations (one per status) which were
      // tripping admin-api 503 under concurrent load. The server-side
      // handler fans out to DDB inside a single Lambda invocation —
      // same work, but only one cold-start surface to the client.
      const statuses: PartnerBriefStatus[] = [
        'human_review_required',
        'staff_review',
        'pending_response',
        'info_requested',
        'pending_validation',
        'pending_reassessment',
        'agent_failed',
        'rejected_poor_fit',
        'rejected_auto',
        'rejected',
        'approved',
        // 30 May 2026 — Active Partners tab folded into this queue (UAT).
        // Fetch the post-approval lifecycle statuses so they surface under
        // the Approved / Onboarding / Active filter pills.
        'pending_backlink_verification',
        'manual_add_invited',
        'manual_add_expired',
        'active',
        'suspended',
        ...(isSuperAdmin ? ['deleted' as PartnerBriefStatus] : []),
      ];
      try {
        const { buckets, cursors, failedZones } = await listAllBriefBuckets(statuses);
        if (cancelled) return;
        const bucket = (s: string) => buckets[s]?.items ?? [];
        setRefreshErrors(0);
        setState({
          status: 'loaded',
          // Fold legacy HRR rows + Request-Info follow-ups into the single
          // review bucket so the operator sees the Brief stay visible after
          // hitting "Send Request Info" (it transitions to pending_response /
          // info_requested per ADR-029 §2).
          reviewItems: [
            ...bucket('staff_review'),
            ...bucket('pending_response'),
            ...bucket('info_requested'),
            ...bucket('human_review_required'),
          ],
          pendingItems: [...bucket('pending_validation'), ...bucket('pending_reassessment')],
          rejectedItems: [
            ...bucket('rejected_poor_fit'),
            ...bucket('rejected_auto'),
            ...bucket('rejected'),
          ],
          closedItems: [...bucket('approved'), ...bucket('pending_backlink_verification')],
          onboardingItems: [...bucket('manual_add_invited'), ...bucket('manual_add_expired')],
          activeItems: [...bucket('active'), ...bucket('suspended')],
          failedItems: bucket('agent_failed'),
          deletedItems: isSuperAdmin ? bucket('deleted') : [],
          reviewCursors: cursors,
          failedZones,
        });
      } catch (err) {
        if (cancelled) return;
        if (isBackground) {
          console.warn('ApplicationsQueue: background poll failed', err);
          setRefreshErrors((n) => n + 1);
          // First failure stays quiet; subsequent ones get a single
          // toast so the operator knows the queue may be stale.
          // The amber banner at >=3 failures stays as a secondary cue.
          return;
        }
        // Initial load failure — show toast + error state (operator
        // has no prior data to look at).
        const t = toastFromError(err, 'Could not load applications');
        toast.push({ variant: 'error', title: t.title, detail: t.detail });
        setState((s) => ({
          ...s,
          status: 'error',
          error: 'Could not load applications. The admin-api may be unavailable — retrying every 5s.',
        }));
      }
    };
    void fetchAll();
    // Only poll while at least one pending_validation row is in the
    // queue. The agent finishes in 5-15s and the Brief transitions to
    // staff_review (or rejected_*); once no rows are pending the queue
    // is stable and the poll stops. Operator-initiated re-assess
    // triggers a fresh pending row → poll restarts via the dep change.
    if (!hasPending) {
      return () => {
        cancelled = true;
      };
    }
    const id = setInterval(() => { void fetchAll({ background: true }); }, 5000);
    // Browsers throttle setInterval to ~1 minute in background tabs,
    // so the 5s poll freezes when the operator switches away. Refresh
    // immediately on tab-visible so the queue catches up instead of
    // showing stale state.
    function onVisibility() {
      if (document.visibilityState === 'visible') {
        void fetchAll({ background: true });
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [hasPending, isSuperAdmin, toast]);

  // Row action handlers — refetch on success to surface the new state.
  // Uses the batched buckets endpoint so one round-trip refreshes
  // everything; if it fails the optimistic state above stays.
  const refetch = async () => {
    const statuses: PartnerBriefStatus[] = [
      'human_review_required',
      'staff_review',
      'pending_response',
      'info_requested',
      'pending_validation',
      'pending_reassessment',
      'agent_failed',
      'rejected_poor_fit',
      'rejected_auto',
      'rejected',
      'approved',
      'pending_backlink_verification',
      'manual_add_invited',
      'manual_add_expired',
      'active',
      'suspended',
      ...(isSuperAdmin ? ['deleted' as PartnerBriefStatus] : []),
    ];
    try {
      const { buckets, cursors, failedZones } = await listAllBriefBuckets(statuses);
      const bucket = (s: string) => buckets[s]?.items ?? [];
      setState((s) => ({
        ...s,
        failedZones,
        reviewItems: [
          ...bucket('staff_review'),
          ...bucket('pending_response'),
          ...bucket('info_requested'),
          ...bucket('human_review_required'),
        ],
        pendingItems: [...bucket('pending_validation'), ...bucket('pending_reassessment')],
        rejectedItems: [
          ...bucket('rejected_poor_fit'),
          ...bucket('rejected_auto'),
          ...bucket('rejected'),
        ],
        closedItems: [...bucket('approved'), ...bucket('pending_backlink_verification')],
        onboardingItems: [...bucket('manual_add_invited'), ...bucket('manual_add_expired')],
        activeItems: [...bucket('active'), ...bucket('suspended')],
        failedItems: bucket('agent_failed'),
        deletedItems: isSuperAdmin ? bucket('deleted') : [],
        reviewCursors: cursors,
      }));
    } catch (err) {
      // Optimistic state stays in place — operator sees their action
      // applied. Next 5s poll will reconcile.
      console.warn('ApplicationsQueue: refetch failed (keeping optimistic state)', err);
    }
  };

  const handleReassess = async (brief: BriefListItem) => {
    setRowBusy(brief.applicationId);
    setRowError(null);
    // Optimistically flip the row's status to pending_reassessment so
    // the operator sees the "Agent running" chip + spinning ↻ icon
    // immediately on click, without waiting ~500ms for the API + refetch
    // to round-trip. The real status from the next refetch overwrites
    // this if the server happens to reject the request.
    setState((s) => ({
      ...s,
      reviewItems: s.reviewItems.filter((b) => b.applicationId !== brief.applicationId),
      failedItems: s.failedItems.filter((b) => b.applicationId !== brief.applicationId),
      closedItems: s.closedItems.filter((b) => b.applicationId !== brief.applicationId),
      pendingItems: [
        ...s.pendingItems,
        // Set reassessRequestedAt locally so the Brief detail page —
        // if the operator clicks through — uses this freshly-now
        // timestamp for the progress bar instead of the original
        // submittedAt (which would be hours/days old).
        { ...brief, status: 'pending_reassessment', reassessRequestedAt: new Date().toISOString() } as BriefListItem,
      ],
    }));
    try {
      // The brief's OWN zone from the fan-out tag — a us-east-1 brief reassessed against
      // ap-southeast-2 would 404 on a row the operator is looking at.
      await reassessBrief(brief.applicationId, zoneByCode(brief.zone));
      toast.push({
        variant: 'success',
        title: `Reassess triggered: ${brief.businessName}`,
        detail: 'Agent will re-run; row stays in pending until it completes.',
      });
      await refetch();
    } catch (err) {
      const t = toastFromError(err, `Could not reassess ${brief.businessName}`);
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
      setRowError(err instanceof Error ? err.message : 'Reassess failed.');
      // Roll back the optimistic move by refetching authoritative state.
      await refetch();
    } finally {
      setRowBusy(null);
    }
  };
  const handleDelete = (brief: BriefListItem) => {
    // Trigger the modal — soft + hard options live inside DeleteBriefModal.
    setDeleteTarget(brief);
  };
  const handleRestore = async (brief: BriefListItem) => {
    setRowBusy(brief.applicationId);
    setRowError(null);
    try {
      await restoreBrief(brief.applicationId, zoneByCode(brief.zone));
      toast.push({
        variant: 'success',
        title: `Restored: ${brief.businessName}`,
      });
      await refetch();
    } catch (err) {
      const t = toastFromError(err, `Could not restore ${brief.businessName}`);
      toast.push({ variant: 'error', title: t.title, detail: t.detail });
      setRowError(err instanceof Error ? err.message : 'Restore failed.');
    } finally {
      setRowBusy(null);
    }
  };

  const handleLoadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const next = await loadMoreStaffReview(state.reviewCursors);
      setState((s) => ({
        ...s,
        reviewItems: [...s.reviewItems, ...next.items],
        reviewCursors: next.cursors,
        // A zone that failed THIS page is reported without discarding the zones that
        // succeeded — the rows that did arrive are still shown.
        failedZones: next.failedZones,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        error: err instanceof Error ? err.message : 'Could not load more.',
      }));
    } finally {
      setLoadingMore(false);
    }
  };

  if (state.status === 'loading') {
    return (
      <section className="portal-applications-section" aria-busy="true">
        <p className="portal-help">Loading applications…</p>
      </section>
    );
  }

  if (state.status === 'error') {
    return (
      <section className="portal-applications-section">
        <div className="portal-error" role="alert">{state.error}</div>
      </section>
    );
  }

  // Sort within the union — comparePriority handles cross-bucket ordering.
  // pending_validation rows sort to the bottom; comparePriority treats
  // unknown statuses with lowest priority by design. Every bucket is
  // always included in allItems; the pill filter is the single source
  // of truth for what's visible. (UAT 30 May 2026 — Active Partners
  // tab folded in; showClosed/showDeleted checkboxes removed as the
  // pills replace them.)
  const allItems = [
    ...state.reviewItems,
    ...state.pendingItems,
    ...state.failedItems,
    ...state.rejectedItems,
    ...state.closedItems,
    ...state.onboardingItems,
    ...state.activeItems,
    ...(isSuperAdmin ? state.deletedItems : []),
  ];
  // Compute group counts BEFORE filter so the pill badge always shows the
  // unfiltered total (lets the operator see how many they're hiding).
  const groupCounts: Record<QueueGroup, number> = {
    pending: 0, review: 0, failed: 0, onboarding: 0, active: 0, rejected_ai: 0, rejected_admin: 0, deleted: 0,
  };
  for (const b of allItems) {
    for (const g of groupsOfBrief(b)) groupCounts[g]++;
  }
  // Apply group filter, then keyword search, then sort. A Brief is
  // visible if ANY of its groups is in the active filter set —
  // supports multi-group membership (e.g. AI rejected appearing in
  // both Review and AI rejected pills). Empty set = nothing matches.
  const groupFiltered = allItems
    .filter((b) => groupsOfBrief(b).some((g) => groupFilter.has(g)))
    // ADR-122 Pattern 1 — the zone filter narrows what is rendered. The pill counts above are
    // deliberately computed BEFORE it, for the same reason they are computed before the group
    // filter: a badge that moved when you narrowed would stop answering "how many are there".
    .filter((b) => matchesZone(b, zoneFilter));
  const q = searchQuery.trim().toLowerCase();
  const filtered = q === ''
    ? groupFiltered
    : groupFiltered.filter((b) =>
        (b.businessName || '').toLowerCase().includes(q) ||
        (b.websiteUrl || '').toLowerCase().includes(q) ||
        (b.applicantEmail || '').toLowerCase().includes(q));
  const merged = sort.key === 'priority'
    ? [...filtered].sort(comparePriority)
    : sortBriefs(filtered, sort);
  const queue = merged.map((b) => b.applicationId);
  // More to load while ANY zone still holds a cursor, not only the home zone.
  const hasMore = Object.values(state.reviewCursors).some(Boolean);

  function toggleSort(key: SortKey) {
    setSort((curr) => {
      if (curr.key !== key) return { key, dir: DEFAULT_DIR[key] };
      // Same column re-clicked → flip direction. Third click on the
      // same column collapses to the natural default for that key.
      const flipped: SortDir = curr.dir === 'asc' ? 'desc' : 'asc';
      return flipped === DEFAULT_DIR[key] ? { key, dir: flipped } : { key, dir: flipped };
    });
  }

  return (
    <section className="portal-applications-section">
      <header className="portal-applications-header">
        <h2>Applications</h2>
        <p className="portal-applications-subtitle">
          Pending Partner Briefs — Stage 1 classification + Stage 2 4-vector rubric per ADR-031.
        </p>
        <div className="portal-applications-controls">
          {/* Keyword search — filters business name + website + email
              (UAT iteration 3). Composes with the pill filter + sort. */}
          <FilterSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search business name, website, or email…"
            ariaLabel="Search Briefs by business name, website, or email"
          />
          <FilterPills
            groups={QUEUE_GROUPS.map((g) => ({ ...g, count: groupCounts[g.key] }))}
            selected={groupFilter}
            onChange={setGroupFilter}
            isSuperAdmin={isSuperAdmin}
            ariaLabel="Filter Briefs by group"
          />
          <ZoneFilter value={zoneFilter} onChange={setZoneFilter} />
        </div>
      </header>

      {/* A zone that did not answer contributes no briefs, and a shorter review queue looks
          exactly like a complete one — an application nobody reviews because nobody knew it
          was there. */}
      <ZoneFanoutWarning
        failedZones={state.failedZones}
        noun="partner applications"
        onRetry={() => void refetch()}
      />

      {rowError && (
        <div className="portal-error" role="alert">{rowError}</div>
      )}

      {refreshErrors >= 3 && (
        <div className="portal-brief-refresh-warning" role="status">
          ⚠ Background refresh is failing (last {refreshErrors} attempts). The list may be
          showing stale data — checking again every 5s.
        </div>
      )}

      {/* Column-header strip — clickable sort headers per UAT 30 May
          2026 (preferred over a dropdown). Each chip shows the column
          name + chevron indicator (▲ asc / ▼ desc / – inactive).
          Priority chip resets to the default cross-bucket order. */}
      <div className="portal-applications-sort-row" role="group" aria-label="Sort Briefs">
        <span className="portal-applications-sort-label">Sort by</span>
        {([
          { key: 'priority' as SortKey, label: 'Priority' },
          { key: 'website' as SortKey, label: 'Website' },
          { key: 'name' as SortKey, label: 'Business' },
          { key: 'submitted' as SortKey, label: 'Submitted' },
          { key: 'score' as SortKey, label: 'Score' },
        ]).map((col) => {
          const active = sort.key === col.key;
          const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
          return (
            <button
              key={col.key}
              type="button"
              className={`portal-sort-header${active ? ' portal-sort-header-active' : ''}`}
              onClick={() => toggleSort(col.key)}
              aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
              title={active ? `Click to flip direction (currently ${sort.dir})` : `Sort by ${col.label}`}
            >
              <span className="portal-sort-header-label">{col.label}</span>
              <span className="portal-sort-header-indicator" aria-hidden="true">{indicator}</span>
            </button>
          );
        })}
      </div>

      {merged.length === 0 ? (
        <p className="portal-applications-empty">
          {groupFilter.size === 0
            ? <>No filter pills are selected. Click a pill above to filter the queue, or <strong>Show all</strong> to see every Brief.</>
            : searchQuery.trim()
              ? <>No Briefs match your search and selected filter pills.</>
              : <>No Briefs match the selected filter pills.</>}
        </p>
      ) : (
        <ul className="portal-applications-list" aria-label="Brief queue">
          {merged.map((brief, idx) => (
            <QueueRow
              key={brief.applicationId}
              brief={brief}
              isSuperAdmin={isSuperAdmin}
              isMutating={me.status === 'loaded' && (me.me?.role === 'super_admin' || me.me?.role === 'nexa_admin')}
              busy={rowBusy === brief.applicationId}
              onReassess={() => void handleReassess(brief)}
              onDelete={() => void handleDelete(brief)}
              onRestore={() => void handleRestore(brief)}
              onOpen={() =>
                // ADR-122 Pattern 2 — the row carries its zone into the detail page's URL,
                // exactly as CustomersPage already does for AdminCustomerDetailPage. Without
                // this the detail page has no way to know which zone to ask, and would fall
                // back to home and 404 on a US brief.
                navigate(`/admin/partners/${encodeURIComponent(brief.applicationId)}${brief.zone ? `?zone=${brief.zone}` : ''}`, {
                  state: {
                    queueContext: {
                      queue,
                      currentIdx: idx,
                      basePath: '/admin/partners',
                      backTo: '/admin/partners',
                    },
                  },
                })
              }
            />
          ))}
        </ul>
      )}

      {hasMore && (
        <div className="portal-applications-pagination">
          <button
            type="button"
            className="portal-link-button-secondary"
            onClick={() => void handleLoadMore()}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
      {deleteTarget && (
        <DeleteBriefModal
          open
          applicationId={deleteTarget.applicationId}
          businessName={deleteTarget.businessName}
          zone={deleteTarget.zone}
          onDeleted={() => {
            setDeleteTarget(null);
            void refetch();
          }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </section>
  );
}

interface QueueRowProps {
  brief: BriefListItem;
  isSuperAdmin: boolean;
  isMutating: boolean;
  busy: boolean;
  onOpen: () => void;
  onReassess: () => void;
  onDelete: () => void;
  onRestore: () => void;
}

function QueueRow({
  brief,
  isSuperAdmin,
  isMutating,
  busy,
  onOpen,
  onReassess,
  onDelete,
  onRestore,
}: QueueRowProps) {
  const classification = classificationOrFallback(brief);
  const isPending = brief.status === 'pending_validation' || brief.status === 'pending_reassessment';
  const isDeleted = brief.status === 'deleted';
  const isFailed = brief.status === 'agent_failed';
  const isClosed = brief.status === 'rejected_poor_fit'
    || brief.status === 'rejected_auto'
    || brief.status === 'rejected'
    || brief.status === 'approved';
  // 30 May 2026 (UAT #23) — once a Brief has been approved (or is past
  // the approval gate in any way), the Stage 1 fit classification pill
  // is redundant noise on the queue row. The Brief detail page keeps
  // the chip (full context lives there). Approval-bucket statuses:
  // approved, pending_backlink_verification, manual_add_invited,
  // manual_add_expired, active, suspended.
  const isPostApproval = brief.status === 'approved'
    || brief.status === 'pending_backlink_verification'
    || brief.status === 'manual_add_invited'
    || brief.status === 'manual_add_expired'
    || brief.status === 'active'
    || brief.status === 'suspended';
  // Stage 1 rejections (rejected_poor_fit) are CATEGORY mismatches,
  // not quality failures. The score of 0 is misleading because it
  // reads identically to agent_failed or a fake-site rejection. Show
  // '—' and an 'Off-target' label so the operator can distinguish
  // 'wrong audience' from 'low quality'.
  const isOffTarget = brief.status === 'rejected_poor_fit';
  const isUat = brief.customerNumber === 'NX-UATEST';
  const scoreBand =
    brief.score == null ? 'none' : brief.score >= 75 ? 'high' : brief.score >= 30 ? 'medium' : 'low';
  const strongFitBonus = brief.strongFitBonusApplied === true || classification === 'strong_fit';

  return (
    <li
      className={`portal-applications-row${isUat ? ' portal-applications-row-uat' : ''}${isDeleted ? ' portal-applications-row-deleted' : ''}${isFailed ? ' portal-applications-row-failed' : ''}${isClosed ? ' portal-applications-row-closed' : ''}`}
      data-status={brief.status}
      data-classification={classification ?? 'unknown'}
    >
      <button
        type="button"
        className="portal-applications-row-main"
        onClick={onOpen}
        aria-label={`Open Brief for ${brief.businessName}`}
      >
        {/* Row layout (30 May 2026 — UAT iteration 2):
            Pills moved OUT of this clickable button into a sibling
            column (.portal-applications-row-pills-column) so the
            contact + meta sub-rows stay constrained to the LEFT column
            and don't bleed under the pill area. Headline now only
            holds the website URL. */}
        <div className="portal-applications-row-headline">
          <span className="portal-applications-row-website" title={brief.businessName}>
            {brief.websiteUrl || brief.businessName}
          </span>
        </div>
        <div className="portal-applications-row-contact">
          <span className="portal-applications-row-company">{brief.businessName}</span>
          <span aria-hidden="true"> · </span>
          <code>{brief.applicantEmail}</code> · <code>{brief.customerNumber}</code> · Submitted{' '}
          {formatDateShort(brief.submittedAt)}
        </div>
        <div className="portal-applications-row-meta">
          {/* ADR-122 Pattern 1 — the Zone column, as a meta field rather than a new grid
              column: this row is a stacked card, not a table, so "column" here means a
              labelled field in the meta line, alongside Jurisdiction. It sits FIRST and
              directly beside Jurisdiction deliberately — the two read as a pair and are
              easily confused, so showing them together is what stops an operator reading a
              business's legal jurisdiction as the zone its data lives in. */}
          <span>
            <strong>Zone:</strong> <ZoneCell zone={brief.zone} />
          </span>
          <span>
            <strong>Jurisdiction:</strong> {brief.jurisdiction || '—'}
          </span>
          <span>
            <strong>Assessment:</strong> {brief.assessmentCount} of 3
          </span>
          {/* Approval timestamp — UAT 30 May 2026. Only renders for
              post-approval rows where reviewedAt was actually set. Format
              matches the existing 'Submitted DATE' style. */}
          {isPostApproval && brief.reviewedAt && (
            <span>
              <strong>Approved:</strong> {formatDateShort(brief.reviewedAt)}
            </span>
          )}
        </div>
      </button>
      {/* Pills column — sits between the clickable row-main button and
          the score/actions aside. Display-only (not clickable). All
          status pills stack here so they occupy a consistent column. */}
      <div className="portal-applications-row-pills-column" aria-label="Brief status pills">
        {isUat && (
          <span className="portal-uat-badge" title="Operator-triggered UAT diagnostic run">
            UAT
          </span>
        )}
        {classification && !isPostApproval && (
          <span
            className={`portal-applications-classification portal-applications-classification-${classification}`}
          >
            {classificationLabel(classification)}
          </span>
        )}
        {isPending && (
          <span className="portal-brief-assessing" title="POW agent currently scoring this Brief">
            <span className="portal-brief-assessing-dot" />
            Agent running
          </span>
        )}
        {isFailed && (
          <span className="portal-applications-failed-flag" role="img" aria-label="Agent failed — needs reassess">
            ⚠ Agent failed
          </span>
        )}
        {/* Rejected pills — Stage 1 off-target, AI auto-reject, admin
            reject. All red via portal-applications-closed-flag-rejected_*
            classes. */}
        {(brief.status === 'rejected_poor_fit'
          || brief.status === 'rejected_auto'
          || brief.status === 'rejected') && (
          <span className={`portal-applications-closed-flag portal-applications-closed-flag-${brief.status}`}>
            {closedStatusLabel(brief.status, brief.reviewedBy)}
          </span>
        )}
        {/* Approval pill — single source of truth for every post-approval
            state (approved, active, suspended, pending_backlink_
            verification, manual_add_invited / _expired). Class is
            authority-based (admin=green, ai=blue) so a legacy 'approved'
            row and a new 'active' row that were BOTH approved by the same
            authority render with the same colour. UAT 30 May 2026 —
            previously the legacy 'approved' status fell through the
            closedStatusLabel branch which used a status-keyed class,
            so legacy AI-approved was green while new AI-approved was
            blue. */}
        {isPostApproval && (
          <span
            className={`portal-applications-closed-flag portal-applications-closed-flag-approval-${brief.reviewedBy ? 'admin' : 'ai'}`}
          >
            {brief.reviewedBy ? '👤 Approved (admin)' : '🤖 Approved (AI)'}
          </span>
        )}
      </div>
      <div className="portal-applications-row-aside">
        <div className="portal-applications-score">
          <div className="portal-applications-score-label">POW Score</div>
          <div
            className={`portal-applications-score-value portal-applications-score-${scoreBand}${isOffTarget ? ' portal-applications-score-offtarget' : ''}`}
            style={{ color: isOffTarget ? undefined : scoreHueColor(brief.score) }}
            title={isOffTarget ? 'Stage 1 rejected as off-target — wrong business category, not a quality measure' : undefined}
          >
            {isOffTarget ? '—' : (brief.score ?? '—')}
            {strongFitBonus && brief.score != null && !isOffTarget && (
              <span className="portal-applications-score-bonus">+20 SF</span>
            )}
          </div>
        </div>
        <div className="portal-applications-row-actions" aria-label="Row actions">
          {!isDeleted && isMutating && (
            <button
              type="button"
              className={`portal-row-action portal-row-action-reassess${(busy || isPending) ? ' portal-row-action-spinning' : ''}`}
              onClick={(e) => { e.stopPropagation(); onReassess(); }}
              disabled={busy || isPending}
              title={isPending ? 'Reassess in progress…' : 'Reassess application'}
              aria-label="Reassess application"
            >
              <span className="portal-reassess-glyph" aria-hidden="true">↻</span>
            </button>
          )}
          {/* UAT fix: soft-delete is operational (accounts:soft_delete = {nexa_admin, super_admin}),
              so use isMutating — same {super, admin} gate as the reassess button above. The deleted
              -brief RECOVERY surface (Show Deleted / restore / purge) stays super-only by design. */}
          {!isDeleted && isMutating && (
            <button
              type="button"
              className="portal-row-action portal-row-action-delete"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              disabled={busy}
              title="Soft-delete this Brief (recoverable via Show Deleted)"
              aria-label="Delete"
            >
              🗑
            </button>
          )}
          {isDeleted && isSuperAdmin && (
            <button
              type="button"
              className="portal-row-action portal-row-action-restore"
              onClick={(e) => { e.stopPropagation(); onRestore(); }}
              disabled={busy}
              title="Restore this Brief to its previous status"
              aria-label="Restore"
            >
              ↺
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function closedStatusLabel(status: string, reviewedBy?: string | null): string {
  switch (status) {
    // AI vs admin differentiation (UAT 30 May 2026). 🤖 marks AI-driven
    // decisions (Stage 1 / Stage 2 agent outputs); 👤 marks human-
    // operator decisions (Approve / Reject button click).
    case 'rejected_poor_fit': return '🤖 Off-target (AI)';
    case 'rejected_auto':     return '🤖 Rejected (AI)';
    case 'rejected':          return '👤 Rejected (admin)';
    // 'approved' is dual-source: agent auto-approve (no reviewedBy) vs
    // admin click Approve (reviewedBy = staffSub). Same DDB status,
    // different decision authority — distinguish by reviewedBy.
    case 'approved':          return reviewedBy ? '👤 Approved (admin)' : '🤖 Approved (AI)';
    default:                  return status;
  }
}

function classificationLabel(c: Stage1Classification): string {
  switch (c) {
    case 'strong_fit':
      return 'Strong Fit';
    case 'possible_fit':
      return 'Possible Fit';
    case 'poor_fit':
      return 'Poor Fit';
  }
}

function formatDateShort(iso: string): string {
  return formatDate(iso, 'medium', currentTimeZone(), iso || '—');
}
